import type { Ingredient, IngredientUnit } from "@/lib/api/types";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";
import { isDoodleBadgeId } from "@/shared/doodles/library";

/**
 * The Doodles badge drawn beside an ingredient — call THIS at every place an
 * ingredient is drawn with a mark (founder, 2026-10-03: "the ingredient takes
 * an icon name so we display the icon").
 *
 * The badge the store PICKED wins when the library can still draw it. With
 * none picked — every ingredient before the column existed, and every one
 * nobody has given an icon — or with an id the library no longer ships, it is
 * the unit's mark, exactly what the row showed before.
 */
export function ingredientBadgeOf(
  ingredient: Pick<Ingredient, "unit" | "badge">,
): DoodleBadgeId {
  return isDoodleBadgeId(ingredient.badge)
    ? ingredient.badge
    : ingredientBadge(ingredient.unit);
}

/**
 * The FALLBACK mark: what an ingredient with no picked badge is drawn with.
 * Draw rows through `ingredientBadgeOf` above, never this directly.
 *
 * The UNIT is the one fact every row has that says what kind of thing it is —
 * weighed, poured or counted — so the fallback follows it and changes when the
 * unit does.
 */
export function ingredientBadge(unit: IngredientUnit): DoodleBadgeId {
  switch (unit) {
    case "g":
    case "kg":
      return "scale";
    case "ml":
      return "bottle";
    case "piece":
      return "package";
  }
}

/** What the row's pill and the level bar say. */
export type StockStatus = "out" | "low" | "ok";

/**
 * Out beats low.
 *
 * `isLowStock` is the SERVER's answer and stays the source of truth for "low" —
 * it is computed against `lowStockThreshold` there, and second-guessing it here
 * would let the badge and the alert disagree. "Out" is the one thing the server
 * does not distinguish, and it is a different fact: below the minimum you have
 * time to order, at zero the recipes that need it stop depleting anything.
 */
export function stockStatusOf(ingredient: Ingredient): StockStatus {
  if (ingredient.onHandQty <= 0) return "out";
  return ingredient.isLowStock ? "low" : "ok";
}

/**
 * How full the bar is, or `null` when there is nothing to measure against.
 *
 * The artboard draws `qty / (min × 2)` — the minimum as the halfway mark, so a
 * bar at 50% reads "right on the alert level". `lowStockThreshold` is NULLABLE,
 * and an ingredient with none has no scale at all: the bar is omitted rather
 * than drawn full, because a full bar is a claim about sufficiency that nobody
 * made.
 */
export function stockLevelPercent(ingredient: Ingredient): number | null {
  const threshold = ingredient.lowStockThreshold;
  if (threshold === null || threshold <= 0) return null;
  const pct = Math.round((ingredient.onHandQty / (threshold * 2)) * 100);
  return Math.max(0, Math.min(100, pct));
}

/** The server's cost precision (`COST_DECIMAL_PLACES`, 4dp). */
const COST_FACTOR = 10 ** 4;

/**
 * The server's `roundCost`, byte for byte — including the `Number.EPSILON`
 * nudge — so a preview can never land a ten-thousandth away from the
 * `avgCostAfter` the purchase then snapshots.
 */
export function roundCost(value: number): number {
  return Math.round((value + Number.EPSILON) * COST_FACTOR) / COST_FACTOR;
}

/**
 * WHY the projected average is what it is — the sentence the restock form puts
 * under the number.
 *
 * - `unknown`: the ingredient has never been purchased (D139, `avgUnitCost`
 *   null); whatever is on the shelf has no cost, so this delivery SETS it.
 * - `emptyShelf`: on hand is zero or below (D3 lets the kitchen cook past
 *   zero), so there is nothing to weigh against and this delivery sets it.
 * - `weighted`: the shelf's known cost and this delivery are averaged.
 */
export type PurchaseCostBasis = "unknown" | "emptyShelf" | "weighted";

export type PurchaseProjection = {
  /** What this delivery costs per unit (the row's `unitCostAtPurchase`). */
  unitCostAtPurchase: number;
  /** The weighted average after it (the row's `avgCostAfter`). */
  avgCostAfter: number;
  basis: PurchaseCostBasis;
};

/**
 * What a purchase would do to the cost — the server's `applyPurchase`
 * (`apps/backend/src/modules/restaurant/policies/weighted-average.policy.ts`)
 * run forward, mirrored exactly (parity spec beside this file):
 *
 * - the unit cost is `roundCost(totalPaid / quantity)` FIRST, and the average
 *   is taken over that rounded unit cost;
 * - existing stock weighs `max(onHand, 0)` and only while its cost is KNOWN
 *   — a negative shelf or an unknown average (D139) weighs nothing, and the
 *   delivery's unit cost becomes the average;
 * - a price of 0 is a real cost (D137): a free lot LOWERS the average, and on
 *   an unknown or empty shelf it sets a known 0;
 * - 4dp, `roundCost`.
 *
 * `null` only when the inputs cannot describe a delivery (no positive
 * quantity, a negative or non-numeric price).
 */
export function projectPurchase(
  ingredient: Pick<Ingredient, "onHandQty" | "avgUnitCost">,
  quantity: number,
  totalPaid: number,
): PurchaseProjection | null {
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(totalPaid) || totalPaid < 0) return null;
  const unitCostAtPurchase = roundCost(totalPaid / quantity);
  const existingWeight =
    ingredient.avgUnitCost === null ? 0 : Math.max(ingredient.onHandQty, 0);
  const totalQty = existingWeight + quantity;
  const basis: PurchaseCostBasis =
    ingredient.avgUnitCost === null
      ? "unknown"
      : existingWeight === 0
        ? "emptyShelf"
        : "weighted";
  const avgCostAfter =
    totalQty <= 0 || existingWeight === 0 || ingredient.avgUnitCost === null
      ? unitCostAtPurchase
      : roundCost(
          (existingWeight * ingredient.avgUnitCost +
            quantity * unitCostAtPurchase) /
            totalQty,
        );
  return { unitCostAtPurchase, avgCostAfter, basis };
}

/** The average a purchase would leave behind — `projectPurchase`'s headline. */
export function projectedUnitCost(
  ingredient: Pick<Ingredient, "onHandQty" | "avgUnitCost">,
  quantity: number,
  totalPaid: number,
): number | null {
  return projectPurchase(ingredient, quantity, totalPaid)?.avgCostAfter ?? null;
}
